// Helpers
import { isArray, isObject, isString } from './typeChecker';

/**
 * Check if a value is empty
 * @param val value to check
 * @returns true if value is empty
 */
// Disable eslint because of the need to use any type
/* eslint-disable-next-line */
export const isEmpty = (val: any): boolean => {
	if (val === null || val === undefined) return true;
	else if (isArray(val)) return !val.length;
	else if (isObject(val)) return Object.keys(val).length === 0;
	else if (isString(val)) return val.trim().length === 0;
	else return false;
}

/**
 * Clone an object or array deeply to remove references
 * @param val value to clone
 * @returns cloned value of val with no references
 */
// Disable eslint because of the need to use any type
/* eslint-disable-next-line */
export const cloneDeep = <TValType extends any>(val: TValType): TValType => {
	if (isEmpty(val)) {
		return val;
	} else if (isArray(val)) {
		// Need to define `as TValType` because the map function return type is TValType[] instead of TValType
		return [
			...val.map(
				(item) => (isArray(item) || isObject(item) ? cloneDeep<typeof item>(item) : item)
			)
		] as TValType;
	} else if (isObject(val)) {
		const obj = Object.create(null);

		for (const key in val) {
			obj[key] = isArray(val[key]) || isObject(val[key]) ? cloneDeep(val[key]) : val[key];
		}

		return { ...obj };
	} else {
		return val;
	}
};

/**
 * A Function to get value from an object and return a default value if not found
 * @param val Object to get value from
 * @param keys array of keys or string of keys separated by '.'
 * @param defaultValue default value to return if value is not found
 */
export const getDefaultValue = <TResultType>(
	val: never, // Use `never` because it's literally can be anything
	keys: string | string[],
	defaultValue: TResultType
): TResultType => {
	if (isEmpty(val) || isEmpty(keys)) return defaultValue;

	const keyList: string[] = isArray(keys) ? [...keys] : keys.split('.');

	let returnedValue = cloneDeep(val);

	while (keyList.length) {
		const key = keyList.shift();

		if (isEmpty(returnedValue[key!])) {
			return defaultValue;
		}

		returnedValue = returnedValue[key!];
	}

	return returnedValue;
};

export const generateID = () => Math.random().toString(36).substring(7);
